import React from 'react';
import { Link } from 'react-router-dom';

const AppSupport = () => {
  const WaveAnimation = () => (
    <div className="flex items-center justify-center space-x-1">
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="w-1 bg-accent-500 rounded-full animate-wave"
          style={{
            height: `${20 + Math.sin(i * 0.5) * 10}px`,
            animationDelay: `${i * 0.1}s`,
          }}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-primary-50">
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            {/* App Icon Placeholder */}
            <div className="mx-auto w-28 h-28 bg-gradient-to-br from-accent-500 to-accent-600 rounded-3xl flex items-center justify-center mb-12 shadow-soft animate-fade-in">
              <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
            </div>
            
            {/* Tagline */}
            <h1 className="text-5xl md:text-7xl font-bold text-neutral-900 mb-8 animate-fade-in">
              Find Your Relief<br />
              <span className="text-primary-500">Through Sound</span>
            </h1>
            
            {/* Description */}
            <p className="text-xl text-neutral-600 max-w-4xl mx-auto mb-12 leading-relaxed animate-fade-in">
              Tinnitus Tracker is a comprehensive iOS application designed to help manage tinnitus symptoms through personalized frequency matching, sound therapy, and progress tracking—all while keeping your data <strong className="text-primary-600">100% private</strong>.
            </p>

            {/* Wave Animation */}
            <div className="mb-8">
              <WaveAnimation />
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-neutral-900 mb-6">Key Features</h2>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">Everything you need for effective tinnitus management</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-neutral-50 to-neutral-100 hover:shadow-soft transition-all duration-300 border border-neutral-200">
              <div className="w-16 h-16 bg-primary-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-card">
                <span className="text-2xl">🎵</span>
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-3">Frequency Matching</h3>
              <p className="text-neutral-600">Interactive 2D control to find and match your tinnitus frequency (20Hz-16kHz range)</p>
            </div>

            {/* Feature 2 */}
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-neutral-50 to-neutral-100 hover:shadow-soft transition-all duration-300 border border-neutral-200">
              <div className="w-16 h-16 bg-accent-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-card">
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-3">Progress Tracking</h3>
              <p className="text-neutral-600">Daily diary to log symptom severity and identify triggers</p>
            </div>

            {/* Feature 3 */}
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-neutral-50 to-neutral-100 hover:shadow-soft transition-all duration-300 border border-neutral-200">
              <div className="w-16 h-16 bg-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-card">
                <span className="text-2xl">🔒</span>
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-3">Complete Privacy</h3>
              <p className="text-neutral-600">All data stays on your device - no accounts, no cloud, no tracking</p>
            </div>

            {/* Feature 4 */}
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-neutral-50 to-neutral-100 hover:shadow-soft transition-all duration-300 border border-neutral-200">
              <div className="w-16 h-16 bg-accent-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-card">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-3">Real-time Audio</h3>
              <p className="text-neutral-600">Professional-grade audio engine with instant response</p>
            </div>

            {/* Feature 5 */}
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-neutral-50 to-neutral-100 hover:shadow-soft transition-all duration-300 border border-neutral-200">
              <div className="w-16 h-16 bg-primary-700 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-card">
                <span className="text-2xl">📱</span>
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-3">Modern Design</h3>
              <p className="text-neutral-600">Clean, accessible interface with intuitive interaction</p>
            </div>

            {/* Feature 6 */}
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-neutral-50 to-neutral-100 hover:shadow-soft transition-all duration-300 border border-neutral-200">
              <div className="w-16 h-16 bg-accent-700 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-card">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-3">Offline First</h3>
              <p className="text-neutral-600">Works completely offline - no internet connection required</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">How It Works</h2>
            <p className="text-xl text-primary-100 max-w-2xl mx-auto">Simple steps to start managing your tinnitus</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-soft">
                <span className="text-3xl font-bold">1</span>
              </div>
              <h3 className="text-2xl font-semibold mb-4">Match Your Frequency</h3>
              <p className="text-primary-100 text-lg leading-relaxed">Use our innovative 2D controller to find the frequency that matches your tinnitus</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-soft">
                <span className="text-3xl font-bold">2</span>
              </div>
              <h3 className="text-2xl font-semibold mb-4">Track Your Progress</h3>
              <p className="text-primary-100 text-lg leading-relaxed">Log daily symptoms to identify patterns and triggers</p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-soft">
                <span className="text-3xl font-bold">3</span>
              </div>
              <h3 className="text-2xl font-semibold mb-4">All Offline, All Private</h3>
              <p className="text-primary-100 text-lg leading-relaxed">No internet required, no data collection, complete peace of mind</p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy First Section */}
      <section className="py-20 bg-neutral-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-12">
            <div className="w-20 h-20 bg-accent-500 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-soft">
              <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-8">Privacy First</h2>
          <p className="text-xl text-neutral-300 leading-relaxed max-w-4xl mx-auto">
            Your health data is personal. That's why Tinnitus Tracker never collects, stores, or transmits any user data. 
            Everything stays on your device, protected by iOS security. We can't access your information because we 
            never collect it in the first place.
          </p>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-neutral-900 mb-12">Need Support?</h2>
          <div className="bg-gradient-to-br from-neutral-50 to-neutral-100 rounded-3xl p-12 border border-neutral-200 shadow-soft">
            <div className="w-16 h-16 bg-primary-500 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-card">
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
            </div>
            <h3 className="text-2xl font-semibold text-neutral-900 mb-6">Get in Touch</h3>
            <p className="text-xl text-neutral-600 mb-8 max-w-2xl mx-auto">Have questions or need assistance? We're here to help.</p>
            <a 
              href="mailto:samchris@naver.com" 
              className="inline-flex items-center bg-primary-500 hover:bg-primary-600 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-200 shadow-soft hover:shadow-lg"
            >
              <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
              samchris@naver.com
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-accent-500 rounded-xl flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
                  </svg>
                </div>
                <span className="text-2xl font-bold">Tinnitus Tracker</span>
              </div>
              <p className="text-neutral-400 text-lg">Professional tinnitus management with complete privacy.</p>
            </div>
            
            <div>
              <h4 className="text-xl font-semibold mb-6">Links</h4>
              <ul className="space-y-3 text-neutral-400">
                <li><Link to="/privacy" className="hover:text-white transition-colors text-lg">Privacy Policy</Link></li>
                <li><Link to="/" className="hover:text-white transition-colors text-lg">App Support</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-xl font-semibold mb-6">Contact</h4>
              <p className="text-neutral-400 text-lg">
                <a href="mailto:samchris@naver.com" className="hover:text-white transition-colors">
                  samchris@naver.com
                </a>
              </p>
            </div>
          </div>
          
          <div className="border-t border-neutral-700 mt-12 pt-8 text-center text-neutral-400">
            <p className="text-lg">&copy; 2025 SaeHyoung Lee. All rights reserved.</p>
            <p className="mt-3 text-neutral-500">Not a substitute for professional medical treatment. Consult your healthcare provider for medical advice.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AppSupport;